import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Loader2, Mail, FileText } from "lucide-react";
import { toast } from "sonner";
import { API, api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import PageHeader, { BackLink } from "@/components/PageHeader";
import Footer from "@/components/landing/Footer";

const SITE = "https://lagooncityrealestate.ca";

export default function BlogPost() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");
  const [reportHtml, setReportHtml] = useState(null);
  const [htmlLoading, setHtmlLoading] = useState(true);
  const [frameH, setFrameH] = useState(700);
  const iframeRef = useRef(null);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const resizeFrame = () => {
    try {
      const d = iframeRef.current.contentWindow.document;
      setFrameH(d.documentElement.scrollHeight + 24);
    } catch (e) {
      /* cross-origin safety no-op */
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get(`/newsletters/${id}`)
      .then((res) => {
        setPost(res.data);
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  }, [id]);

  useEffect(() => {
    setHtmlLoading(true);
    api
      .get(`/newsletters/${id}/html`)
      .then((res) => setReportHtml(res.data.html))
      .catch(() => setReportHtml(null))
      .finally(() => setHtmlLoading(false));
  }, [id]);

  const subscribe = async (e) => {
    e.preventDefault();
    setSubscribing(true);
    try {
      const res = await api.post("/subscribe", { email });
      if (!res.data.already) trackEvent("newsletter_subscribe", { source: "blog_post" });
      toast.success(
        res.data.already
          ? "You're already on the list — see you next edition."
          : "Welcome aboard — the latest edition is on its way to your inbox."
      );
      setEmail("");
    } catch {
      toast.error("Could not subscribe right now — please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  if (status === "loading") {
    return (
      <div className="bg-[#0D0B0E] min-h-screen text-slate-100">
        <PageHeader />
        <div data-testid="blogpost-loading" className="max-w-4xl mx-auto px-6 py-32 flex items-center gap-3 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Loading edition…
        </div>
      </div>
    );
  }

  if (status === "error" || !post) {
    return (
      <div className="bg-[#0D0B0E] min-h-screen text-slate-100">
        <PageHeader />
        <div data-testid="blogpost-not-found" className="max-w-4xl mx-auto px-6 py-32 text-center">
          <FileText className="mx-auto text-[#E890A7]" size={40} />
          <h1 className="font-display text-4xl mt-6">Edition not found</h1>
          <Link to="/blog" className="inline-block mt-8 rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-7 py-3.5 hover:bg-[#F2A7BA] transition-colors">
            Back to the journal
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const canonical = `${SITE}/blog/${post.newsletter_id}`;
  const title = `${post.month_name} ${post.year} Lagoon City Market Report | Joselle Alves`;
  const desc = `${post.title} — read Joselle Alves' ${post.month_name} ${post.year} Lagoon City market report: fresh waterfront listings, canal market stats and community notes for Brechin & Lake Simcoe.`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: `${post.month_name} ${post.year} — Lagoon City Market Report`,
    description: desc,
    datePublished: post.created_at,
    url: canonical,
    author: { "@type": "Person", name: "Joselle Alves" },
    publisher: { "@type": "Organization", name: "Lagoon City Real Estate" },
    image: `${SITE}/assets/joselle-portrait.jpg`,
  };

  return (
    <div className="bg-[#0D0B0E] min-h-screen text-slate-100">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={`${SITE}/assets/joselle-portrait.jpg`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <PageHeader />

      <main className="max-w-4xl mx-auto px-6 py-12 sm:py-16">
        <BackLink to="/blog" label="All editions" />

        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-8"
        >
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#E890A7]/80 mb-4">
            {post.month_name} {post.year} · Market Report
          </p>
          <h1 data-testid="blogpost-title" className="font-display font-light text-4xl sm:text-5xl leading-tight">
            The Lagoon City <em className="italic text-[#E890A7]">{post.month_name}</em> report
          </h1>
          <p className="mt-5 text-slate-400 font-light max-w-2xl">
            {post.title} — Joselle's monthly read on the Lagoon City waterfront market: newly listed
            homes and canal properties, price movement, and what's happening around the community.
          </p>

          <div className="mt-8 flex flex-wrap gap-4">
            <a
              data-testid="blogpost-view-btn"
              href={`${API}/newsletters/${post.newsletter_id}/file`}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackEvent("view_market_report", { edition: `${post.month_name} ${post.year}`, source: "blog_post" })}
              className="rounded-full border border-[#E890A7]/35 text-[#F2A7BA] px-7 py-3.5 flex items-center gap-2 hover:bg-[#E890A7]/10 transition-all duration-300"
            >
              <FileText size={16} /> Open original PDF
            </a>
          </div>

          {htmlLoading ? (
            <div data-testid="blogpost-html-loading" className="mt-10 flex items-center gap-3 text-slate-500">
              <Loader2 className="animate-spin" size={18} /> Rendering the report…
            </div>
          ) : reportHtml ? (
            <iframe
              ref={iframeRef}
              data-testid="blogpost-report-html"
              title={`${post.month_name} ${post.year} market report`}
              srcDoc={reportHtml}
              onLoad={resizeFrame}
              style={{ width: "100%", height: `${frameH}px`, border: "none" }}
              className="mt-10 rounded-3xl overflow-hidden bg-white shadow-2xl shadow-black/40"
            />
          ) : (
            <div className="mt-10 rounded-3xl overflow-hidden border border-[#E890A7]/15 bg-[#151218]">
              <iframe
                data-testid="blogpost-pdf"
                title={`${post.month_name} ${post.year} market report`}
                src={`${API}/newsletters/${post.newsletter_id}/file`}
                className="w-full h-[70vh]"
              />
            </div>
          )}

          <div
            data-testid="blogpost-subscribe"
            className="mt-12 rounded-[2rem] border border-[#E890A7]/25 bg-gradient-to-r from-[#1E1A23] to-[#151218] p-8 sm:p-10"
          >
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#E890A7] mb-3 flex items-center gap-2">
              <Mail size={13} /> Inbox delivery
            </p>
            <h2 className="font-display text-2xl sm:text-3xl">Never miss an edition</h2>
            <p className="text-sm text-slate-400 mt-2 font-light max-w-lg">
              Get each new Lagoon City market report the moment it's published — one email a month, no spam.
            </p>
            <form onSubmit={subscribe} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-lg">
              <input
                data-testid="blogpost-subscribe-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="flex-1 rounded-full bg-[#0D0B0E]/70 border border-[#E890A7]/25 px-5 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E890A7]/70 transition-colors"
              />
              <button
                data-testid="blogpost-subscribe-btn"
                type="submit"
                disabled={subscribing}
                className="rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-7 py-3.5 text-sm flex items-center justify-center gap-2 hover:bg-[#F2A7BA] disabled:opacity-60 transition-colors"
              >
                {subscribing && <Loader2 size={15} className="animate-spin" />}
                {subscribing ? "Joining…" : "Subscribe"}
              </button>
            </form>
          </div>
        </motion.article>
      </main>

      <Footer />
    </div>
  );
}
